import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, RouterOutlet } from '@angular/router';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { DividerModule } from 'primeng/divider';
import { SelectModule } from 'primeng/select';
import { SessionsModule } from '../../modules/besharp-sessions/besharp-sessions.module';
import { BesharpSidebarComponent, BesharpSidebarConfig } from '../../modules/besharp-sidebar/component/besharp-sidebar.component';
import { BesharpTopbarComponent, BesharpTopbarItems, mobileMenuListItem } from '../../modules/besharp-topbar/besharp-topbar.component';
import { BesharpBreadcrumbsComponent } from "../../modules/besharp-breadcrumbs/besharp-breadcrumbs.component";
import { AicTokenModalComponent } from '../../pages/aic-token-modal/aic-token-modal.component';
import { ThemeService } from '../../services/theme.service';
import { Subscription } from 'rxjs';
import { fetchAuthSession } from 'aws-amplify/auth';

const MOBILE_BREAKPOINT = 768;

@Component({
  selector: 'app-base-layout',
  templateUrl: './base-layout.component.html',
  styleUrl: './base-layout.component.scss',
  imports: [
    CommonModule,
    RouterOutlet,
    RouterModule,
    MenuModule,
    BesharpSidebarComponent,
    BesharpTopbarComponent,
    SessionsModule,
    DividerModule,
    SelectModule,
    BesharpBreadcrumbsComponent,
    AicTokenModalComponent,
  ],
})
export class BaseLayoutComponent implements OnInit, OnDestroy {
  // Topbar Settings
  protected title = 'OTA Portal';
  protected logo = '/logo.png';
  protected homeRoute = '/app/home';
  protected topBarItems!: BesharpTopbarItems;
  protected mobileMenu = true;
  protected mobileMenuListItems!: mobileMenuListItem[];

  // Sidebar Settings
  protected sidebarConfiguration!: BesharpSidebarConfig;

  // Internal Values
  protected backgroundImage = '/background-top.png';

  // AIC Token Modal
  showAicTokenModal = false;
  userId = '';

  collapsed = false;
  isMobile = false;
  private themeSubscription!: Subscription;
  private userEmail: string = '';
  private userGroups: string[] = [];
  private isGlobalAdmin: boolean = false;
  private isStrictlyGlobalAdmin: boolean = false;

  private get topBarMenuItems(): MenuItem[] {
    return [
      ...(this.isGlobalAdmin ? [
        {
          label: 'User Management',
          icon: 'pi pi-users',
          command: () => this.navigateToUsers(),
        },
      ] : []),
      ...(this.isStrictlyGlobalAdmin ? [
        {
          label: 'Application Settings',
          icon: 'pi pi-cog',
          command: () => this.router.navigate(['app', 'config']),
        },
      ] : []),
      {
        label: 'AIC Token',
        icon: 'pi pi-key',
        command: () => this.handleAICToken(),
      },
    ];
  }

  constructor(private router: Router, private themeService: ThemeService) {}

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkScreenSize(event.target.innerWidth);
  }

  ngOnInit(): void {

    this.checkScreenSize(window.innerWidth);
    this.loadUserEmail();

    this.updateTopBarItems();

    this.themeSubscription = this.themeService.darkMode$.subscribe(() => {
      this.updateTopBarItems();
    });
  }

  ngOnDestroy(): void {
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe();
    }
  }

  private updateTopBarItems(): void {
    const isDarkMode = this.themeService.getDarkMode();
    this.topBarItems = {
      itemsSize: 'xs',
      items: [
        {
          icon: isDarkMode ? 'pi pi-moon' : 'pi pi-sun',
          iconPosition: 'left' as const,
          outlined: true,
          command: () => this.toggleDarkMode(),
          severity: 'success' as const,
        },
        ...[{
          icon: 'pi pi-users',
          iconPosition: 'left' as const,
          outlined: true,
          severity: 'danger' as const,
          command: () => {},
          menuItems: this.topBarMenuItems,
        }],
      ],
    };
  }

  toggleDarkMode(): void {
    this.themeService.toggleDarkMode();
  }

  navigateToUsers(): void {
    this.router.navigate(['/app/users']);
  }

  handleAICToken(): void {
    this.showAicTokenModal = true;
  }

  onAicTokenSaved(): void {
    const refreshEvent = new CustomEvent('refreshTable', { detail: { timestamp: Date.now() } });
    window.dispatchEvent(refreshEvent);
  }

  getUserEmail(): string {
    return this.userEmail || '';
  }

  private async loadUserEmail(): Promise<void> {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      
      if (idToken) {
        const payload = JSON.parse(atob(idToken.toString().split('.')[1]));
        this.userEmail = payload.email || 'Unknown';
        this.userId = payload.sub || '';
        this.userGroups = payload['cognito:groups'] || [];
        this.isGlobalAdmin = this.userGroups.some(group => 
          group.includes('global-admin') || group.includes('sw-manager') || group.includes('technician-admin')
        );
        this.isStrictlyGlobalAdmin = this.userGroups.some(group =>
          group.includes('global-admin')
        );
        this.updateTopBarItems();
        this.updateSidebarAndMobileMenu();
        
      } else {
        this.userEmail = 'Not authenticated';
        this.userGroups = [];
        this.isGlobalAdmin = false;
        this.isStrictlyGlobalAdmin = false;
      }
    } catch (error) {
      console.error('Error loading user email:', error);
      this.userEmail = 'Error loading email';
      this.userGroups = [];
      this.isGlobalAdmin = false;
      this.isStrictlyGlobalAdmin = false;
    }
  }

  private updateSidebarAndMobileMenu(): void {
    this.sidebarConfiguration = {
      items: [
        { label: 'Sections', icon: 'pi pi-table', category: true },
        { label: 'Fleet Management', command: () => this.router.navigate(['app', 'fleet-management']) },
        { label: 'Software Updates', command: () => this.router.navigate(['app', 'software-updates']) },
        { label: 'Deploy Management', command: () => this.router.navigate(['app', 'deploy-management']) },
        { label: 'Log Management', command: () => this.router.navigate(['app', 'log-management']) },
        { divider: true },
        ...(this.isGlobalAdmin ? [
          { label: 'Administration', icon: 'pi pi-cog', category: true },
          { label: 'User Management', command: () => this.router.navigate(['app', 'users']) },
          ...(this.isStrictlyGlobalAdmin ? [
            { label: 'Application Settings', command: () => this.router.navigate(['app', 'config']) },
          ] : []),
          { divider: true },
        ] : []),
        { spacer: true },
      ],
    };

    this.mobileMenuListItems = [
      { icon: 'pi pi-list', label: 'Fleet Management', command: () => this.router.navigate(['app', 'fleet-management']) },
      { icon: 'pi pi-upload', label: 'Software Updates', command: () => this.router.navigate(['app', 'software-updates']) },
      { icon: 'pi pi-send', label: 'Deploy Management', command: () => this.router.navigate(['app', 'deploy-management']) },
      { icon: 'pi pi-list', label: 'Log Management', command: () => this.router.navigate(['app', 'log-management']) },
      ...(this.isGlobalAdmin ? [
        { icon: 'pi pi-users', label: 'User Management', command: () => this.router.navigate(['app', 'users']) },
      ] : []),
      ...(this.isStrictlyGlobalAdmin ? [
        { icon: 'pi pi-cog', label: 'Application Settings', command: () => this.router.navigate(['app', 'config']) },
      ] : []),
    ];
  }

  onSidebarCollapseChanged(collapsed: boolean) {
    this.collapsed = collapsed;
  }

  private checkScreenSize(width: number): void {
    this.isMobile = width <= MOBILE_BREAKPOINT;
  }
}
